import * as React from "react";
import { useState, useMemo } from "react";

import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import SearchIcon from "@material-ui/icons/Search";
import LinkIcon from "@material-ui/icons/Link";
import RefreshIcon from "@material-ui/icons/Refresh";

import { Notification, useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigPost } from "../getConfig";

type BrandTestStatus = "SUCCESS" | "NO_URL" | "ERROR";
type PingStatus =
  | "SUCCESS"
  | "HTTP_ERROR"
  | "TIMEOUT"
  | "NETWORK_ERROR"
  | "SKIPPED";

interface IBrandTestResult {
  merchant_id: string;
  merchant_name: string;
  category: string | null;
  status: BrandTestStatus;
  click_id: string | null;
  tracking_url: string | null;
  tracking_mobile_url: string | null;
  ping_status: PingStatus;
  ping_response_code: number;
  ping_duration_ms: number;
  ping_attempts: number;
  click_duration_ms: number;
  error: string | null;
  is_failure: boolean;
  failure_reason: string | null;
}

interface IBrandUrlTestSummary {
  total_brands: number;
  success_count: number;
  no_url_count: number;
  error_count: number;
  ping_success_count: number;
  ping_failure_count: number;
  failed_count: number;
  retried_count: number;
  ping_max_retries: number;
  duration_ms: number;
  concurrency: number;
}

type BrandUrlSyncActionType = "blacklisted" | "re_enabled" | "skipped";

interface IBrandUrlSyncAction {
  action: BrandUrlSyncActionType;
  merchant_id: string;
  merchant_name: string;
  test_result: "PASS" | "FAIL";
  previous_reason_code: string | null;
  previous_watch: boolean | null;
  new_reason: string | null;
  new_reason_code: string | null;
  new_watch: boolean | null;
  detail: string;
}

interface IBrandUrlSyncSummary {
  config_enabled: boolean;
  applied: boolean;
  skipped_reason: string | null;
  blacklisted_count: number;
  re_enabled_count: number;
  skipped_count: number;
  error_count: number;
}

interface IVdxBrandUrlTestResponse {
  success: boolean;
  message: string;
  summary: IBrandUrlTestSummary;
  /**
   * The backend only returns failures here to keep the payload small for
   * sweeps over thousands of brands. The complete set of rows (successes +
   * failures) is always in `csv` for download.
   */
  failed_results: IBrandTestResult[];
  csv: string;
  csv_filename: string;
  sync: {
    summary: IBrandUrlSyncSummary;
    actions: IBrandUrlSyncAction[];
    csv: string | null;
    csv_filename: string | null;
  };
  email: {
    sent: boolean;
    recipients: string[];
    error: string | null;
  };
}

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    padding: "2em",
    backgroundColor: "#f5f7fa",
  },
  mainHeading: {
    textAlign: "center",
    marginBottom: "0.25em",
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  mainSubheading: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    marginBottom: "2em",
  },
  configCard: {
    padding: "1.5em",
    marginBottom: "1.5em",
    borderRadius: 12,
  },
  metadataContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "1em",
    marginBottom: "1.5em",
    flexWrap: "wrap",
  },
  metadataCard: {
    minWidth: 160,
    textAlign: "center",
    borderRadius: 12,
  },
  metadataValue: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  metadataLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  metadataValueSuccess: { color: "#2e7d32" },
  metadataValueWarn: { color: "#f57c00" },
  metadataValueError: { color: "#c62828" },
  primaryButton: {
    padding: "10px 22px",
    fontSize: "0.95rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 8,
  },
  warningBox: {
    backgroundColor: "#fff3e0",
    border: "1px solid #ffb74d",
    borderRadius: 8,
    padding: "1em",
    marginBottom: "1em",
  },
  warningText: { color: "#e65100", fontSize: "0.9rem" },
  tableContainer: {
    marginTop: "1em",
    width: "100%",
    overflowX: "auto",
  },
  table: { minWidth: 1100 },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
  },
  failureRow: {
    backgroundColor: "#fff5f5",
  },
  noDataMessage: {
    textAlign: "center",
    color: theme.palette.grey[500],
    fontStyle: "italic",
    margin: "2em 0",
  },
  emailStatusOk: {
    backgroundColor: "#e8f5e9",
    border: "1px solid #81c784",
    borderRadius: 8,
    padding: "0.75em 1em",
    marginTop: "0.5em",
  },
  emailStatusErr: {
    backgroundColor: "#ffebee",
    border: "1px solid #ef9a9a",
    borderRadius: 8,
    padding: "0.75em 1em",
    marginTop: "0.5em",
  },
  statusChipSuccess: { backgroundColor: "#4caf50", color: "white" },
  statusChipNoUrl: { backgroundColor: "#ff9800", color: "white" },
  statusChipError: { backgroundColor: "#f44336", color: "white" },
  pingChipSuccess: { backgroundColor: "#4caf50", color: "white" },
  pingChipFailure: { backgroundColor: "#f44336", color: "white" },
  pingChipSkipped: { backgroundColor: "#9e9e9e", color: "white" },
  truncatedUrl: {
    maxWidth: 280,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "inline-block",
    verticalAlign: "middle",
  },
}));

const statusChipClassFor = (
  status: BrandTestStatus,
  classes: ReturnType<typeof useStyles>
): string => {
  switch (status) {
    case "SUCCESS":
      return classes.statusChipSuccess;
    case "NO_URL":
      return classes.statusChipNoUrl;
    case "ERROR":
    default:
      return classes.statusChipError;
  }
};

const pingChipClassFor = (
  status: PingStatus,
  classes: ReturnType<typeof useStyles>
): string => {
  if (status === "SUCCESS") return classes.pingChipSuccess;
  if (status === "SKIPPED") return classes.pingChipSkipped;
  return classes.pingChipFailure;
};

const formatDuration = (ms: number): string => {
  if (!ms || ms < 0) return "0 ms";
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(2)} s`;
  const mins = Math.floor(seconds / 60);
  const remSec = Math.round(seconds % 60);
  return `${mins}m ${remSec}s`;
};

const downloadCsv = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const VdxBrandUrlTest = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [skipPing, setSkipPing] = useState<boolean>(false);
  const [concurrency, setConcurrency] = useState<string>("10");
  const [pingMaxRetries, setPingMaxRetries] = useState<string>("2");
  const [merchantIdsInput, setMerchantIdsInput] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);
  const [response, setResponse] = useState<IVdxBrandUrlTestResponse | null>(
    null
  );

  const [search, setSearch] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [pingFilter, setPingFilter] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);

  // The backend only returns failures in `failed_results`; the full CSV is
  // available via the download button.
  const failedResults: IBrandTestResult[] = response?.failed_results || [];

  const filteredResults = useMemo(() => {
    let rows = failedResults;
    if (statusFilter) rows = rows.filter((r) => r.status === statusFilter);
    if (pingFilter) rows = rows.filter((r) => r.ping_status === pingFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          r.merchant_id.toLowerCase().includes(q) ||
          r.merchant_name.toLowerCase().includes(q) ||
          (r.category || "").toLowerCase().includes(q)
      );
    }
    return rows;
  }, [failedResults, statusFilter, pingFilter, search]);

  const pagedResults = useMemo(
    () =>
      filteredResults.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [filteredResults, page, rowsPerPage]
  );

  const handleRun = async () => {
    const concurrencyNum = parseInt(concurrency, 10);
    if (
      !Number.isFinite(concurrencyNum) ||
      concurrencyNum < 1 ||
      concurrencyNum > 25
    ) {
      notify("Concurrency must be an integer between 1 and 25", "error");
      return;
    }

    const pingMaxRetriesNum = parseInt(pingMaxRetries, 10);
    if (
      !Number.isFinite(pingMaxRetriesNum) ||
      pingMaxRetriesNum < 0 ||
      pingMaxRetriesNum > 5
    ) {
      notify("Ping max retries must be an integer between 0 and 5", "error");
      return;
    }

    const merchant_ids = merchantIdsInput
      .split(/[\s,;\n]+/)
      .map((m) => m.trim())
      .filter((m) => m.length > 0);

    const body: any = {
      send_email: sendEmail,
      skip_ping: skipPing,
      concurrency: concurrencyNum,
      ping_max_retries: pingMaxRetriesNum,
    };
    if (merchant_ids.length > 0) body.merchant_ids = merchant_ids;

    setLoading(true);
    setResponse(null);
    setPage(0);

    try {
      const headers = getConfigPost();
      const res = await axios.post<IVdxBrandUrlTestResponse>(
        ENDPOINTS.VDX_BRAND_URL_TEST,
        body,
        { headers, timeout: 10 * 60 * 1000 }
      );

      if (res.data && res.data.success) {
        setResponse(res.data);
        notify(res.data.message || "VDX brand URL test completed", "info");
        if (sendEmail) {
          const recipients = res.data.email?.recipients || [];
          if (res.data.email?.sent) {
            notify(
              recipients.length > 0
                ? `Report emailed to ${recipients.join(", ")}`
                : "Report emailed.",
              "info",
              "",
              undefined,
              7000
            );
          } else if (res.data.email?.error) {
            notify(
              `Email dispatch failed: ${res.data.email.error}`,
              "warning",
              "",
              undefined,
              8000
            );
          }
        }
      } else {
        notify(
          (res.data && res.data.message) ||
            "VDX brand URL test returned an unexpected response",
          "error"
        );
      }
    } catch (error: any) {
      notify(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to run VDX brand URL test",
        "error",
        "",
        undefined,
        7000
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCsv = () => {
    if (!response?.csv) {
      notify("No CSV available to download", "warning");
      return;
    }
    downloadCsv(
      response.csv,
      response.csv_filename || "vdx-brand-url-test.csv"
    );
  };

  const handleDownloadSyncCsv = () => {
    if (!response?.sync?.csv) {
      notify("No sync CSV available to download", "warning");
      return;
    }
    downloadCsv(
      response.sync.csv,
      response.sync.csv_filename || "vdx-brand-url-test-sync.csv"
    );
  };

  const renderConfig = () => (
    <Paper className={classes.configCard} elevation={2}>
      <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: "0.5em" }}>
        <LinkIcon color="primary" />
        <Typography variant="h6">Run Configuration</Typography>
      </Box>

      <Box className={classes.warningBox}>
        <Typography className={classes.warningText}>
          This will generate a fresh VDx tracking URL for <strong>every enabled-on-app brand</strong>{" "}
          plus <strong>active blacklisted brands</strong>, using the configured test user,
          optionally ping each URL (with retries), and produce a downloadable CSV report.
          Only failed brands are shown in the failures table – the full set of rows is always
          in the test CSV. When <strong>vdx.brand_url_test_sync</strong> is enabled in backend
          config (and ping is not skipped), failed enabled brands are auto-blacklisted and
          watched blacklist entries that pass may be auto re-enabled. The sweep can take a few
          minutes depending on brand count, concurrency and retries.
        </Typography>
      </Box>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label="Concurrency"
            type="number"
            value={concurrency}
            onChange={(e) => setConcurrency(e.target.value)}
            inputProps={{ min: 1, max: 25, step: 1 }}
            helperText="1 - 25 (default 10)"
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label="Ping Max Retries"
            type="number"
            value={pingMaxRetries}
            onChange={(e) => setPingMaxRetries(e.target.value)}
            inputProps={{ min: 0, max: 5, step: 1 }}
            helperText="0 - 5 extra attempts (default 2)"
            disabled={loading || skipPing}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={4}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label="Merchant IDs (optional)"
            placeholder="Comma / space / newline separated. Leave empty to test all enabled brands."
            value={merchantIdsInput}
            onChange={(e) => setMerchantIdsInput(e.target.value)}
            helperText="Useful for retries. Leave blank to test every enabled VDx brand."
            disabled={loading}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <FormControlLabel
            control={
              <Switch
                checked={skipPing}
                onChange={(e) => setSkipPing(e.target.checked)}
                color="primary"
                disabled={loading}
              />
            }
            label="Skip Ping"
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                color="primary"
                disabled={loading}
              />
            }
            label="Email Report"
          />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleRun}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={18} thickness={3} style={{ color: "white" }} />
                ) : (
                  <PlayArrowIcon />
                )
              }
              className={classes.primaryButton}
            >
              {loading ? "Running Sweep…" : "Run Brand URL Test"}
            </Button>

            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={handleDownloadCsv}
              disabled={loading || !response?.csv}
              startIcon={<CloudDownloadIcon />}
              className={classes.primaryButton}
            >
              Download Test CSV
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              size="large"
              onClick={handleDownloadSyncCsv}
              disabled={loading || !response?.sync?.csv}
              startIcon={<CloudDownloadIcon />}
              className={classes.primaryButton}
            >
              Download Sync CSV
            </Button>

            {response && (
              <Button
                variant="text"
                color="default"
                size="large"
                onClick={handleRun}
                disabled={loading}
                startIcon={<RefreshIcon />}
                className={classes.primaryButton}
              >
                Re-Run
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>

      {response?.email && (response.email.sent || response.email.error) && (
        <Box
          className={
            response.email.sent ? classes.emailStatusOk : classes.emailStatusErr
          }
        >
          {response.email.sent ? (
            <Typography variant="body2" style={{ color: "#2e7d32" }}>
              <strong>Email sent</strong>
              {response.email.recipients && response.email.recipients.length > 0
                ? ` to ${response.email.recipients.join(", ")}`
                : ""}
              . The full CSV is attached.
            </Typography>
          ) : (
            <Typography variant="body2" style={{ color: "#c62828" }}>
              <strong>Email failed:</strong> {response.email.error}
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );

  const renderSummary = () => {
    if (!response) return null;
    const { summary } = response;
    return (
      <div className={classes.metadataContainer}>
        <Card className={classes.metadataCard}>
          <CardContent>
            <Typography className={classes.metadataValue}>
              {summary.total_brands.toLocaleString()}
            </Typography>
            <Typography className={classes.metadataLabel}>Total Tested</Typography>
          </CardContent>
        </Card>
        <Card className={classes.metadataCard}>
          <CardContent>
            <Typography
              className={`${classes.metadataValue} ${classes.metadataValueSuccess}`}
            >
              {summary.success_count.toLocaleString()}
            </Typography>
            <Typography className={classes.metadataLabel}>URLs Generated</Typography>
          </CardContent>
        </Card>
        <Card className={classes.metadataCard}>
          <CardContent>
            <Typography
              className={`${classes.metadataValue} ${classes.metadataValueSuccess}`}
            >
              {summary.ping_success_count.toLocaleString()}
            </Typography>
            <Typography className={classes.metadataLabel}>Pings OK</Typography>
          </CardContent>
        </Card>
        <Card className={classes.metadataCard}>
          <CardContent>
            <Typography
              className={`${classes.metadataValue} ${classes.metadataValueWarn}`}
            >
              {summary.no_url_count.toLocaleString()}
            </Typography>
            <Typography className={classes.metadataLabel}>No URL</Typography>
          </CardContent>
        </Card>
        <Card className={classes.metadataCard}>
          <CardContent>
            <Typography
              className={`${classes.metadataValue} ${classes.metadataValueError}`}
            >
              {summary.error_count.toLocaleString()}
            </Typography>
            <Typography className={classes.metadataLabel}>Click Errors</Typography>
          </CardContent>
        </Card>
        <Card className={classes.metadataCard}>
          <CardContent>
            <Typography
              className={`${classes.metadataValue} ${classes.metadataValueError}`}
            >
              {summary.ping_failure_count.toLocaleString()}
            </Typography>
            <Typography className={classes.metadataLabel}>Pings Failed</Typography>
          </CardContent>
        </Card>
        <Card className={classes.metadataCard}>
          <CardContent>
            <Typography
              className={`${classes.metadataValue} ${classes.metadataValueError}`}
            >
              {summary.failed_count.toLocaleString()}
            </Typography>
            <Typography className={classes.metadataLabel}>Failed Brands</Typography>
          </CardContent>
        </Card>
        <Card className={classes.metadataCard}>
          <CardContent>
            <Typography
              className={`${classes.metadataValue} ${classes.metadataValueWarn}`}
            >
              {summary.retried_count.toLocaleString()}
            </Typography>
            <Typography className={classes.metadataLabel}>Retried</Typography>
          </CardContent>
        </Card>
        <Card className={classes.metadataCard}>
          <CardContent>
            <Typography className={classes.metadataValue}>
              {formatDuration(summary.duration_ms)}
            </Typography>
            <Typography className={classes.metadataLabel}>Duration</Typography>
          </CardContent>
        </Card>
        <Card className={classes.metadataCard}>
          <CardContent>
            <Typography className={classes.metadataValue}>
              {summary.concurrency}
            </Typography>
            <Typography className={classes.metadataLabel}>Concurrency</Typography>
          </CardContent>
        </Card>
        <Card className={classes.metadataCard}>
          <CardContent>
            <Typography className={classes.metadataValue}>
              {summary.ping_max_retries}
            </Typography>
            <Typography className={classes.metadataLabel}>Max Retries</Typography>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderSyncSummary = () => {
    if (!response?.sync) return null;
    const { summary, actions } = response.sync;

    if (!summary.config_enabled) {
      return (
        <Paper className={classes.configCard} elevation={1}>
          <Typography variant="subtitle1" gutterBottom>
            Blacklist sync
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {summary.skipped_reason ||
              "brand_url_test_sync is disabled in backend config — no blacklist changes were applied."}
          </Typography>
        </Paper>
      );
    }

    return (
      <>
        <Typography
          variant="h6"
          style={{ marginBottom: "0.75em", textAlign: "center" }}
        >
          Blacklist Sync
          {!summary.applied && summary.skipped_reason
            ? ` (${summary.skipped_reason})`
            : ""}
        </Typography>
        <div className={classes.metadataContainer}>
          <Card className={classes.metadataCard}>
            <CardContent>
              <Typography
                className={`${classes.metadataValue} ${classes.metadataValueError}`}
              >
                {summary.blacklisted_count.toLocaleString()}
              </Typography>
              <Typography className={classes.metadataLabel}>Auto-Blacklisted</Typography>
            </CardContent>
          </Card>
          <Card className={classes.metadataCard}>
            <CardContent>
              <Typography
                className={`${classes.metadataValue} ${classes.metadataValueSuccess}`}
              >
                {summary.re_enabled_count.toLocaleString()}
              </Typography>
              <Typography className={classes.metadataLabel}>Re-Enabled</Typography>
            </CardContent>
          </Card>
          <Card className={classes.metadataCard}>
            <CardContent>
              <Typography
                className={`${classes.metadataValue} ${classes.metadataValueWarn}`}
              >
                {summary.skipped_count.toLocaleString()}
              </Typography>
              <Typography className={classes.metadataLabel}>Skipped</Typography>
            </CardContent>
          </Card>
          <Card className={classes.metadataCard}>
            <CardContent>
              <Typography className={classes.metadataValue}>
                {summary.error_count.toLocaleString()}
              </Typography>
              <Typography className={classes.metadataLabel}>Sync Errors</Typography>
            </CardContent>
          </Card>
        </div>

        {summary.applied && actions.length > 0 && (
          <Paper className={classes.tableContainer} style={{ marginBottom: "1.5em" }}>
            <Box style={{ padding: "0.75em 1em" }}>
              <Typography variant="subtitle1">
                <strong>{actions.length}</strong> sync action(s) — full detail in Sync CSV.
              </Typography>
            </Box>
            <TableContainer>
              <Table className={classes.table} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeader}>Action</TableCell>
                    <TableCell className={classes.tableHeader}>Merchant</TableCell>
                    <TableCell className={classes.tableHeader}>Test</TableCell>
                    <TableCell className={classes.tableHeader}>Detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {actions.slice(0, 50).map((a) => (
                    <TableRow key={`${a.merchant_id}-${a.action}-${a.detail}`}>
                      <TableCell>
                        <Chip
                          size="small"
                          label={a.action}
                          color={
                            a.action === "blacklisted"
                              ? "secondary"
                              : a.action === "re_enabled"
                                ? "primary"
                                : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {a.merchant_name}
                        <Typography variant="caption" display="block" color="textSecondary">
                          {a.merchant_id}
                        </Typography>
                      </TableCell>
                      <TableCell>{a.test_result}</TableCell>
                      <TableCell>{a.detail}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {actions.length > 50 && (
              <Typography
                variant="body2"
                style={{ padding: "0.75em 1em", color: "#555" }}
              >
                Showing first 50 actions. Download Sync CSV for the full list.
              </Typography>
            )}
          </Paper>
        )}
      </>
    );
  };

  const renderFilters = () => {
    if (!response) return null;
    return (
      <Paper className={classes.configCard} elevation={1}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Search by ID, name or category"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              label="Click Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              SelectProps={{ native: false }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="SUCCESS">SUCCESS</MenuItem>
              <MenuItem value="NO_URL">NO_URL</MenuItem>
              <MenuItem value="ERROR">ERROR</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              label="Ping Status"
              value={pingFilter}
              onChange={(e) => {
                setPingFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="SUCCESS">SUCCESS</MenuItem>
              <MenuItem value="HTTP_ERROR">HTTP_ERROR</MenuItem>
              <MenuItem value="TIMEOUT">TIMEOUT</MenuItem>
              <MenuItem value="NETWORK_ERROR">NETWORK_ERROR</MenuItem>
              <MenuItem value="SKIPPED">SKIPPED</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <Tooltip title="Clear filters">
              <span>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("");
                    setPingFilter("");
                    setPage(0);
                  }}
                >
                  Clear Filters
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderRow = (r: IBrandTestResult) => {
    const maxAttempts =
      (response?.summary.ping_max_retries ?? 0) + 1;
    const attemptLabel =
      r.ping_attempts > 0 ? `${r.ping_attempts} / ${maxAttempts}` : "-";
    return (
      <TableRow
        key={`${r.merchant_id}-${r.click_id ?? "noclick"}`}
        className={classes.failureRow}
      >
        <TableCell>{r.merchant_id}</TableCell>
        <TableCell>{r.merchant_name}</TableCell>
        <TableCell>{r.category || "-"}</TableCell>
        <TableCell>
          <Chip
            size="small"
            label={r.status}
            className={statusChipClassFor(r.status, classes)}
          />
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={r.ping_status}
            className={pingChipClassFor(r.ping_status, classes)}
          />
        </TableCell>
        <TableCell align="right">
          <Tooltip
            title={
              r.ping_attempts > 1
                ? `Retried ${r.ping_attempts - 1} time(s) before failing`
                : ""
            }
          >
            <span>{attemptLabel}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          {r.ping_response_code > 0 ? r.ping_response_code : "-"}
        </TableCell>
        <TableCell align="right">
          {r.click_duration_ms > 0 ? `${r.click_duration_ms} ms` : "-"}
        </TableCell>
        <TableCell align="right">
          {r.ping_duration_ms > 0 ? `${r.ping_duration_ms} ms` : "-"}
        </TableCell>
        <TableCell>
          {r.tracking_url ? (
            <Tooltip title={r.tracking_url}>
              <Link
                href={r.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.truncatedUrl}
              >
                {r.tracking_url}
              </Link>
            </Tooltip>
          ) : (
            <Typography variant="caption" color="textSecondary">
              -
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {r.failure_reason ? (
            <Typography variant="body2" style={{ color: "#c62828" }}>
              {r.failure_reason}
            </Typography>
          ) : r.error ? (
            <Typography variant="body2" style={{ color: "#c62828" }}>
              {r.error}
            </Typography>
          ) : (
            <Typography variant="caption" color="textSecondary">
              -
            </Typography>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const renderResultsTable = () => {
    if (!response) return null;

    if (failedResults.length === 0) {
      return (
        <Paper className={classes.tableContainer} style={{ padding: "1.5em" }}>
          <Typography
            variant="h6"
            style={{ textAlign: "center", color: "#2e7d32" }}
          >
            All tested brands passed – no failures to display.
          </Typography>
          <Typography
            variant="body2"
            style={{ textAlign: "center", color: "#555", marginTop: 8 }}
          >
            The full per-brand report (successes + failures) is available via{" "}
            <strong>Download CSV</strong>.
          </Typography>
        </Paper>
      );
    }

    if (filteredResults.length === 0) {
      return (
        <Typography className={classes.noDataMessage}>
          No failures match the current filters.
        </Typography>
      );
    }

    return (
      <Paper className={classes.tableContainer}>
        <Box style={{ padding: "0.75em 1em" }}>
          <Typography variant="subtitle1">
            <strong>{failedResults.length}</strong> failed brand(s) – the full
            list (including successes) is in the downloadable CSV.
          </Typography>
        </Box>
        <TableContainer>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}>Merchant ID</TableCell>
                <TableCell className={classes.tableHeader}>Name</TableCell>
                <TableCell className={classes.tableHeader}>Category</TableCell>
                <TableCell className={classes.tableHeader}>Status</TableCell>
                <TableCell className={classes.tableHeader}>Ping</TableCell>
                <TableCell className={classes.tableHeader} align="right">
                  Attempts
                </TableCell>
                <TableCell className={classes.tableHeader} align="right">
                  HTTP
                </TableCell>
                <TableCell className={classes.tableHeader} align="right">
                  Click ms
                </TableCell>
                <TableCell className={classes.tableHeader} align="right">
                  Ping ms
                </TableCell>
                <TableCell className={classes.tableHeader}>Tracking URL</TableCell>
                <TableCell className={classes.tableHeader}>Failure / Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{pagedResults.map(renderRow)}</TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredResults.length}
          page={page}
          onChangePage={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100, 250]}
        />
      </Paper>
    );
  };

  return (
    <div className={classes.main}>
      <Typography variant="h4" component="h1" className={classes.mainHeading}>
        VDX Brand URL Test
      </Typography>
      <Typography variant="body1" className={classes.mainSubheading}>
        Verify every enabled VDX brand still returns a working tracking URL.
      </Typography>

      {renderConfig()}
      {renderSummary()}
      {renderSyncSummary()}
      {renderFilters()}
      {renderResultsTable()}

      {!response && !loading && (
        <Typography className={classes.noDataMessage}>
          Configure the options above and click <strong>Run Brand URL Test</strong> to
          start a sweep. A summary, the list of <strong>failed brands</strong>, and a
          downloadable CSV (with every tested brand) will appear here.
        </Typography>
      )}

      <Notification />
    </div>
  );
};

export default VdxBrandUrlTest;
